"use client";

import React, {
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";

import { cn } from "@perceptron/utils/cn";
import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  PlayerSrc,
  useMediaState,
  VideoMimeType,
} from "@vidstack/react";
import { isArray, isString } from "lodash-es";

import { AnnotationsOverlay } from "./components/overlay/AnnotationsOverlay";
import { TrackingInspectorProvider } from "./tracking/inspector/controller";
import type { NanoModelUrls } from "./tracking/contracts";
import type { FrameRecoveryProvider } from "./tracking/recovery/contracts";

export type {
  FrameRecoveryProvider,
  RecoveryInput,
  RecoveryOutcome,
  RecoveryImage,
  RecoveryUsage,
} from "./tracking/recovery/contracts";
import { TrackingInspector } from "./tracking/inspector/TrackingInspector";
import type { TimelinePlayerBridge } from "./bridge";
import { useVidstackBridge } from "./engine/vidstack";
import { usePlayerShortcuts } from "./hooks/usePlayerShortcuts";
import { useTimelineFocusLifecycle } from "./hooks/useTimelineFocusLifecycle";
import { useSeekWhenReady } from "./hooks/useSeekWhenReady";
import { useFrameStepping } from "./hooks/useFrameStepping";
import MediaControls from "./MediaControls";
import MediaControlsMobileOverlay from "./MediaControlsMobileOverlay";
import { createTimelineStore } from "./store/timeline";
import { TimelineStoreProvider, TimelineDataStoreProvider, useTimelineDataStore } from "./store/timeline-context";
import { createTimelineDataStore } from "./store/timeline-data";
import { TimelineEditor } from "./TimelineEditor";
import { TTimelineItem, TNodeUpdate } from "./types";
import { focusTargetId, isTimelineFocusTarget } from "./utils/focus";
import { findNodeById } from "./utils/tree";
import { adjacentSegmentTime, segmentStartTimes } from "./utils/segment-navigation";
import type { TimelineZoom } from "./utils/viewport";
import { KeyboardShortcutsGuide } from "./KeyboardShortcutsGuide";
import "@vidstack/react/player/styles/base.css";

// Types
export * from "./types";

// Bridge
export type { TimelinePlayerBridge } from "./bridge";

// Engine
export { useVidstackBridge } from "./engine/vidstack";

// Stores
export type { TimelineStore } from "./store/timeline";
export { createTimelineStore } from "./store/timeline";
export type { TimelineDataStore, TimelineDataStoreCallbacks } from "./store/timeline-data";
export { createTimelineDataStore } from "./store/timeline-data";
export {
  TimelineStoreProvider,
  TimelineDataStoreProvider,
  useTimelineStore,
  useTimelineDataStore,
} from "./store/timeline-context";

// Hooks
export { useTimelineState } from "./hooks/useTimelineState";
export { useTimelineRows } from "./hooks/useTimelineRows";
export { useTimelineViewport } from "./hooks/useTimelineViewport";
export { usePlayerShortcuts, DEFAULT_SHORTCUTS } from "./hooks/usePlayerShortcuts";
export type { PlayerShortcut } from "./hooks/usePlayerShortcuts";
export { useSpatialSegments } from "./hooks/useSpatialSegments";
export type {
  PreparedPointBox,
  PreparedPoint,
  PreparedPolygon,
  PreparedSpatialSegment,
} from "./components/overlay/types";

export type { BoxCoordinates, DisplayBounds } from "./utils/coordinates";
export { coordinatesToDisplayBounds } from "./utils/coordinates";

// Tree utilities
export { findNodeById, findParent, isContainer } from "./utils/tree";

/** Tracks videos that are not on screen, into the cache a player hydrates from. */
export { BackgroundTrackingWarmer } from "./tracking/runtime/background-warmer";
export type { WarmTarget } from "./tracking/runtime/background-warmer";

/** Waypoint, decoded-frame and cluster ids all address the same track. */
export { focusTargetId } from "./utils/focus";

// Constants
export { BOUNDING_BOX } from "./constants";

export const transformPlayerSrc = (src: { src: string; type: string }): PlayerSrc => {
  if (isString(src)) {
    return src;
  }

  if (isArray(src)) {
    throw new Error("Invalid src");
  }

  let internalType = undefined;
  const videoType: string = src.type;

  if (videoType === "video/quicktime") {
    // Most browsers don't support QuickTime MIME type but can play H.264-encoded
    // .mov files when declared as mp4 (both use the same underlying codec)
    internalType = "video/mp4";
  } else {
    internalType = videoType as VideoMimeType;
  }

  return {
    src: src.src,
    type: internalType,
  } as PlayerSrc;
};
const NO_SHORTCUTS = [] as const;
export interface VideoPlayerHandle {
  seekTo: (time: number) => void;
  setHighlightedSegmentIds: (segmentIds: string[]) => void;
  setSelectedSegmentId: (segmentId: string | null) => void;
  setMinimized: (minimized: boolean) => void;
  getMinimized: () => boolean;
}

export interface VideoEditorProps {
  // Required
  src: { src: string; type: string };
  title?: string;
  timelineData: TTimelineItem[];

  // Optional configuration
  initialDuration?: number;
  initialPixelsPerSecond?: number;
  /** Enables internal tracking inspection; host apps own developer-mode activation. */
  developerMode?: boolean;
  /** App-owned model recovery, enabled by default when a provider is supplied. */
  recoveryProvider?: FrameRecoveryProvider;
  /** App-specific model hosting; omitted values keep the shared defaults. */
  nanoModelUrls?: NanoModelUrls;

  // Callbacks — node-id addressing. Every node (track, collection, segment)
  // has a globally unique id; CRUD events are reported by that id alone.
  onNodeUpdate?: (nodeId: string, updates: TNodeUpdate) => void;
  onNodeAdd?: (parentId: string | null, node: TTimelineItem) => void;
  onNodeRemove?: (nodeId: string) => void;
  onSegmentSelect?: (segmentId: string | null) => void;
  onDurationChange?: (duration: number) => void;
  onHighlightedSegmentIdsChange?: (segmentIds: string[]) => void;
  /** Whether tracking has intervals in flight for the current source. */
  onTrackingBusyChange?: (busy: boolean) => void;
}

const Timeline = ({
  bridge,
  playerRef,
  zoomRef,
  onDurationChange,
}: {
  bridge: TimelinePlayerBridge;
  playerRef: React.RefObject<MediaPlayerInstance | null>;
  zoomRef: React.RefObject<((zoom: TimelineZoom) => void) | null>;
  onDurationChange?: (duration: number) => void;
}) => {
  const duration = useMediaState("duration", playerRef);

  const setDuration = useTimelineDataStore((state) => state.setDuration);

  useEffect(() => {
    if (!isNaN(duration) && duration > 0) {
      setDuration(duration);
      onDurationChange?.(duration);
    }
  }, [duration, setDuration, onDurationChange]);

  return (
    <div className={cn("flex h-full w-full flex-col justify-center rounded-2xl")}>
      <MediaControls bridge={bridge} className="hidden md:flex" playerRef={playerRef} />
      <div className="mb-2 hidden md:block" />
      <TimelineEditor bridge={bridge} zoomRef={zoomRef} />
    </div>
  );
};

export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoEditorProps>(
  (
    {
      src,
      title,
      timelineData,
      initialDuration = 0,
      initialPixelsPerSecond = 50,
      developerMode = false,
      recoveryProvider,
      nanoModelUrls,
      onNodeUpdate,
      onNodeAdd,
      onNodeRemove,
      onSegmentSelect,
      onDurationChange,
      onHighlightedSegmentIdsChange,
      onTrackingBusyChange,
    },
    ref,
  ) => {
    const playerRef = useRef<MediaPlayerInstance>(null);
    const timelineStore = useMemo(
      () => createTimelineStore(initialPixelsPerSecond),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    const engineBridge = useVidstackBridge(playerRef);
    const stepFrame = useFrameStepping(src.src, engineBridge);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const shortcutsReturnFocus = useRef<HTMLElement | null>(null);
    const showShortcuts = useCallback(() => {
      shortcutsReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setShortcutsOpen(true);
    }, []);
    useEffect(() => {
      if (!developerMode) setShortcutsOpen(false);
    }, [developerMode]);
    const segmentNavigation = useRef({ items: timelineData, initialDuration });
    useLayoutEffect(() => {
      segmentNavigation.current = { items: timelineData, initialDuration };
    }, [timelineData, initialDuration]);
    const skipSegment = useCallback(
      (direction: -1 | 1) => {
        const { items, initialDuration } = segmentNavigation.current;
        const starts = segmentStartTimes(items, timelineStore.getState().trackingTimeline);
        const duration = engineBridge.getDuration?.();
        const target = adjacentSegmentTime(
          starts,
          engineBridge.getCurrentTime(),
          direction,
          duration && Number.isFinite(duration) ? duration : initialDuration,
        );
        if (target !== undefined) engineBridge.seek(target);
      },
      [engineBridge, timelineStore],
    );
    // The mounted timeline editor installs its zoom handler here.
    const timelineZoomRef = useRef<((zoom: TimelineZoom) => void) | null>(null);
    const zoomTimeline = useCallback((zoom: TimelineZoom) => timelineZoomRef.current?.(zoom), []);
    const bridge = useMemo(
      () => ({
        ...engineBridge,
        stepFrame,
        seekBoundaryFrame: stepFrame,
        skipSegment,
        zoomTimeline,
        showShortcuts: developerMode ? showShortcuts : undefined,
      }),
      [developerMode, engineBridge, showShortcuts, skipSegment, stepFrame, zoomTimeline],
    );
    const getShortcutScope = useCallback(() => playerRef.current?.el, []);
    usePlayerShortcuts(bridge, shortcutsOpen ? NO_SHORTCUTS : undefined, getShortcutScope);

    const seekWhenReady = useSeekWhenReady(playerRef, src.src, bridge.seek);

    useImperativeHandle(
      ref,
      () => ({
        seekTo: seekWhenReady,
        setHighlightedSegmentIds: (segmentIds: string[]) => {
          const state = timelineStore.getState();
          const current = state.highlightedSegmentIds;
          // Host sync reflects the emitted array back to us. A fresh array can
          // be an intentional label hover over the same canonical track.
          if (current === segmentIds) return;
          const eligibleIds = segmentIds.filter((id) =>
            isTimelineFocusTarget(findNodeById(timelineData, focusTargetId(id))),
          );
          state.setHighlightedSegmentIds(eligibleIds.length === segmentIds.length ? segmentIds : eligibleIds);
        },
        setSelectedSegmentId: (segmentId: string | null) => {
          const id = segmentId === null ? null : focusTargetId(segmentId);
          timelineStore
            .getState()
            .selectSegment(id && isTimelineFocusTarget(findNodeById(timelineData, id)) ? id : null);
        },
        setMinimized: (minimized: boolean) => {
          timelineStore.getState().setIsMinimized(minimized);
        },
        getMinimized: () => timelineStore.getState().isMinimized,
      }),
      [seekWhenReady, timelineStore, timelineData],
    );

    // Create scoped stores - only once per component instance
    // This preserves UI state (zoom, collapsed tracks, selection) across data updates
    const timelineDataStore = useMemo(
      () =>
        createTimelineDataStore(timelineData, initialDuration, {
          onNodeUpdate,
          onNodeAdd,
          onNodeRemove,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    // Attach host bridges before data/source cleanup can clear focus.
    // Subscribe to selection changes
    useEffect(() => {
      if (!onSegmentSelect) return;

      let prevSegmentId = timelineStore.getState().selectedSegmentId;
      const unsubscribe = timelineStore.subscribe((state) => {
        const currentSegmentId = state.selectedSegmentId;
        if (currentSegmentId !== prevSegmentId) {
          prevSegmentId = currentSegmentId;
          onSegmentSelect(currentSegmentId);
        }
      });
      return unsubscribe;
    }, [timelineStore, onSegmentSelect]);

    // Subscribe to highlighted segment changes (for bidirectional sync)
    useEffect(() => {
      if (!onHighlightedSegmentIdsChange) return;

      let prevIds: string[] = timelineStore.getState().highlightedSegmentIds;
      const unsubscribe = timelineStore.subscribe((state) => {
        const currentIds = state.highlightedSegmentIds;
        if (currentIds !== prevIds) {
          prevIds = currentIds;
          onHighlightedSegmentIdsChange(currentIds);
        }
      });
      return unsubscribe;
    }, [timelineStore, onHighlightedSegmentIdsChange]);

    useTimelineFocusLifecycle(src.src, timelineStore, timelineDataStore);

    // Sync timeline data when initialData prop changes (e.g., streaming updates)
    // This updates the data state while preserving UI state
    useEffect(() => {
      const { setItems } = timelineDataStore.getState();
      setItems(timelineData);
    }, [timelineData, timelineDataStore]);

    // Sync duration when initialDuration prop changes
    // Only update if explicitly provided (> 0), otherwise let video player set it
    useEffect(() => {
      if (initialDuration > 0) {
        const { setDuration } = timelineDataStore.getState();
        setDuration(initialDuration);
      }
    }, [initialDuration, timelineDataStore]);

    return (
      <TimelineDataStoreProvider value={timelineDataStore}>
        <TimelineStoreProvider value={timelineStore}>
          <TrackingInspectorProvider enabled={developerMode}>
            <MediaPlayer
              className={cn(
                "flex h-full flex-col gap-2 rounded-2xl",
                developerMode && "relative",
                "dark",
                "border border-border bg-gradient-to-t from-background-alt-2 via-background-alt-2 via-[80%] to-[#32302D]",
                "dark:from-background-alt-2 dark:via-background-alt-2 dark:to-background-alt-2",
              )}
              crossOrigin="anonymous"
              data-video-player
              keyDisabled
              playsInline
              ref={playerRef}
              src={transformPlayerSrc(src)}
              title={title}
              onKeyDown={(event) => {
                if (event.key === "Escape" && !event.defaultPrevented) {
                  timelineStore.getState().selectSegment(null);
                }
              }}
            >
              <div
                className="relative mt-2 flex h-full w-[calc(100%-16px)] items-center justify-center overflow-hidden rounded-xl bg-black"
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (target === event.currentTarget || target.closest("[data-video-surface]")) {
                    timelineStore.getState().selectSegment(null);
                  }
                }}
              >
                <MediaProvider
                  className="h-full w-full [&>video]:h-full [&>video]:w-full [&>video]:object-contain"
                  data-video-surface
                />
                <AnnotationsOverlay
                  nanoModelUrls={nanoModelUrls}
                  recoveryProvider={recoveryProvider}
                  seek={bridge.seek}
                  src={src.src}
                  subscribeToTime={bridge.subscribeToTime}
                  onTrackingBusyChange={onTrackingBusyChange}
                />
                <MediaControlsMobileOverlay bridge={bridge} className="md:hidden" playerRef={playerRef} />
              </div>
              <div className={cn("max-h-[max(40%,350px)] w-full px-4")}>
                <Timeline
                  bridge={bridge}
                  playerRef={playerRef}
                  zoomRef={timelineZoomRef}
                  onDurationChange={onDurationChange}
                />
              </div>
              <TrackingInspector bridge={bridge} />
              {developerMode && (
                <KeyboardShortcutsGuide
                  container={playerRef.current?.el}
                  open={shortcutsOpen}
                  returnFocus={shortcutsReturnFocus.current}
                  onOpenChange={setShortcutsOpen}
                />
              )}
            </MediaPlayer>
          </TrackingInspectorProvider>
        </TimelineStoreProvider>
      </TimelineDataStoreProvider>
    );
  },
);
VideoPlayer.displayName = "VideoPlayer";
